import { useEffect, useState, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Bug, Lightbulb, Send, Loader2, ArrowUp, MessageSquare, Paperclip, X,
  Map, ListChecks, PlusSquare,
} from 'lucide-react'
import { reportsAPI } from '@/lib/api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import PageHeader from '@/components/PageHeader'
import { useAuth } from '@/hooks/useAuth'

const STATUS_META = {
  open:         { label: 'Open',           color: 'bg-blue-500/10 text-blue-400 border-blue-500/30' },
  in_progress:  { label: 'In behandeling', color: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30' },
  done:         { label: 'Afgewerkt',      color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' },
  dismissed:    { label: 'Afgewezen',      color: 'bg-muted text-muted-foreground border-border' },
}

export default function FeedbackPage() {
  const { user, isAdmin } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const initialTab = searchParams.get('id') ? 'roadmap' : 'submit'
  const [tab, setTab] = useState(initialTab)
  const [detailId, setDetailId] = useState(Number(searchParams.get('id')) || null)

  const openDetail = (id) => {
    setDetailId(id)
    const q = new URLSearchParams(searchParams)
    q.set('id', String(id))
    setSearchParams(q, { replace: true })
  }
  const closeDetail = () => {
    setDetailId(null)
    const q = new URLSearchParams(searchParams)
    q.delete('id')
    setSearchParams(q, { replace: true })
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <PageHeader
        icon={Bug}
        title="Bug / idee-bus"
        subtitle="Meld bugs, deel ideeën, stem op wat je belangrijk vindt."
      />

      {detailId ? (
        <ReportDetail id={detailId} onClose={closeDetail} currentUser={user} isAdmin={isAdmin} />
      ) : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="submit"><PlusSquare className="h-4 w-4 mr-1" />Indienen</TabsTrigger>
            <TabsTrigger value="roadmap"><Map className="h-4 w-4 mr-1" />Roadmap</TabsTrigger>
            <TabsTrigger value="mine"><ListChecks className="h-4 w-4 mr-1" />Mijn indieningen</TabsTrigger>
          </TabsList>
          <TabsContent value="submit"  className="mt-4"><SubmitTab /></TabsContent>
          <TabsContent value="roadmap" className="mt-4"><RoadmapTab onOpen={openDetail} /></TabsContent>
          <TabsContent value="mine"    className="mt-4"><MineTab onOpen={openDetail} /></TabsContent>
        </Tabs>
      )}
    </div>
  )
}

/* ─────────── Indienen ─────────── */
function SubmitTab() {
  const [type, setType] = useState('bug')
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [files, setFiles] = useState([])
  const [busy, setBusy] = useState(false)
  const [feedback, setFeedback] = useState(null)

  const verzend = async (e) => {
    e.preventDefault()
    if (!title.trim()) { setFeedback({ type: 'err', text: 'Geef eerst een titel.' }); return }
    setBusy(true); setFeedback(null)
    try {
      const r = await reportsAPI.create({ type, title: title.trim(), body: body.trim() || null })
      if (files.length) await reportsAPI.uploadFiles(r.data.id, files)
      setFeedback({ type: 'ok', text: 'Bedankt — verzonden!' })
      setTitle(''); setBody(''); setFiles([])
    } catch (e) {
      setFeedback({ type: 'err', text: e.response?.data?.error || e.message })
    } finally { setBusy(false) }
  }

  return (
    <Card>
      <CardContent className="p-4 space-y-4">
        <form onSubmit={verzend} className="space-y-3">
          <div className="flex gap-2">
            <button type="button" onClick={() => setType('bug')}
                    className={`flex-1 flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${
                      type === 'bug' ? 'border-signal bg-signal/10 text-signal' : 'border-border hover:border-signal/50'
                    }`}>
              <Bug className="h-4 w-4" /> Bug melden
            </button>
            <button type="button" onClick={() => setType('idea')}
                    className={`flex-1 flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${
                      type === 'idea' ? 'border-acid bg-acid/10 text-acid' : 'border-border hover:border-acid/50'
                    }`}>
              <Lightbulb className="h-4 w-4" /> Idee delen
            </button>
          </div>
          <div>
            <Label htmlFor="fb-title" className="text-xs">Titel *</Label>
            <Input id="fb-title" value={title} onChange={e => setTitle(e.target.value)}
                   placeholder={type === 'bug' ? 'Waar loopt het mis?' : 'Wat zou je willen zien?'}
                   maxLength={200} disabled={busy} className="mt-1" />
          </div>
          <div>
            <Label htmlFor="fb-body" className="text-xs">Omschrijving (optioneel)</Label>
            <Textarea id="fb-body" value={body} onChange={e => setBody(e.target.value)}
                      placeholder={type === 'bug'
                        ? 'Wat gebeurde er? Wat verwachtte je? Op welke pagina?'
                        : 'Wat zou dit oplossen of verbeteren?'}
                      rows={4} maxLength={5000} disabled={busy} className="mt-1" />
          </div>
          <div>
            <Label className="text-xs">Screenshots (optioneel, max 3 × 5 MB)</Label>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <label className="inline-flex items-center gap-1 h-9 px-3 text-sm rounded-md border border-input bg-background cursor-pointer hover:border-acid/50 transition-colors">
                <Paperclip className="h-3.5 w-3.5" /> Kies bestand(en)
                <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" multiple className="hidden"
                       onChange={e => setFiles(Array.from(e.target.files || []).slice(0, 3))}
                       disabled={busy} />
              </label>
              {files.map((f, i) => (
                <span key={i} className="text-[10px] pf-mono text-muted-foreground truncate max-w-[10rem]">
                  {f.name} ({Math.round(f.size / 1024)} KB)
                </span>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button type="submit" disabled={busy || !title.trim()}>
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
              Verzenden
            </Button>
            {feedback && (
              <span className={`text-xs ${feedback.type === 'ok' ? 'text-live' : 'text-signal'}`}>
                {feedback.text}
              </span>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

/* ─────────── Roadmap ─────────── */
function RoadmapTab({ onOpen }) {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(null)

  const laad = useCallback(() => {
    setLoading(true)
    reportsAPI.public()
      .then(r => setRows(r.data.reports || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }, [])
  useEffect(() => { laad() }, [laad])

  const vote = async (id) => {
    setBusy(id)
    try { await reportsAPI.toggleUpvote(id); laad() }
    catch (e) { window.alert(e.response?.data?.error || e.message) }
    finally { setBusy(null) }
  }

  const groups = { in_progress: [], open: [], done: [] }
  for (const r of rows) if (groups[r.status]) groups[r.status].push(r)

  const label = { in_progress: 'In behandeling', open: 'Open', done: 'Afgewerkt' }

  return (
    <div className="space-y-4">
      {loading && !rows.length && <p className="text-sm text-muted-foreground">Laden…</p>}
      {Object.entries(groups).map(([key, list]) => list.length > 0 && (
        <div key={key}>
          <h3 className="pf-mono text-[11px] uppercase tracking-widest text-muted-foreground mb-2">
            {label[key]} ({list.length})
          </h3>
          <div className="space-y-2">
            {list.map(r => (
              <RoadmapRow key={r.id} r={r} onOpen={() => onOpen(r.id)}
                          onVote={() => vote(r.id)} busy={busy === r.id} />
            ))}
          </div>
        </div>
      ))}
      {!loading && !rows.length && <p className="text-sm text-muted-foreground">Nog geen ideeën/bugs op de roadmap.</p>}
    </div>
  )
}

function RoadmapRow({ r, onOpen, onVote, busy }) {
  const meta = STATUS_META[r.status] || STATUS_META.open
  return (
    <div className="rounded-md border border-border/60 bg-card/50 p-3 flex items-start gap-3">
      <button type="button" onClick={onVote} disabled={busy}
              className={`flex flex-col items-center min-w-[3rem] py-1 rounded transition-colors ${
                r.my_vote ? 'bg-acid/10 text-acid border border-acid/40'
                          : 'text-muted-foreground border border-border hover:border-acid/40'
              }`}
              title={r.my_vote ? 'Klik om je stem terug te nemen' : 'Upvote'}>
        <ArrowUp className="h-4 w-4" />
        <span className="text-sm font-semibold">{r.upvotes}</span>
      </button>
      <button type="button" onClick={onOpen} className="flex-1 min-w-0 text-left">
        <div className="flex items-center gap-2 flex-wrap">
          {r.type === 'bug' ? <Bug className="h-3.5 w-3.5 text-signal" /> : <Lightbulb className="h-3.5 w-3.5 text-acid" />}
          <span className="font-semibold text-sm">{r.title}</span>
          <Badge className={`text-[10px] ${meta.color}`}>{meta.label}</Badge>
          {r.comment_count > 0 && (
            <span className="pf-mono text-[10px] text-muted-foreground flex items-center gap-1">
              <MessageSquare className="h-3 w-3" />{r.comment_count}
            </span>
          )}
        </div>
        {r.body && <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{r.body}</p>}
        <p className="text-[10px] text-muted-foreground mt-1">
          door {r.display_name || r.username} · {new Date(r.created_at).toLocaleDateString('nl-BE')}
        </p>
      </button>
    </div>
  )
}

/* ─────────── Mijn indieningen ─────────── */
function MineTab({ onOpen }) {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    reportsAPI.mine()
      .then(r => setRows(r.data.reports || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }, [])
  return (
    <div className="space-y-2">
      {loading && !rows.length && <p className="text-sm text-muted-foreground">Laden…</p>}
      {!loading && !rows.length && <p className="text-sm text-muted-foreground">Je hebt nog niks ingediend.</p>}
      {rows.map(r => {
        const meta = STATUS_META[r.status] || STATUS_META.open
        return (
          <button key={r.id} type="button" onClick={() => onOpen(r.id)}
                  className="w-full text-left rounded-md border border-border/60 bg-card/50 p-3 hover:border-acid/40 transition-colors">
            <div className="flex items-center gap-2 flex-wrap">
              {r.type === 'bug' ? <Bug className="h-3.5 w-3.5 text-signal" /> : <Lightbulb className="h-3.5 w-3.5 text-acid" />}
              <span className="font-semibold text-sm">{r.title}</span>
              <Badge className={`text-[10px] ${meta.color}`}>{meta.label}</Badge>
              {r.upvotes > 0 && (
                <span className="pf-mono text-[10px] text-acid flex items-center gap-1">
                  <ArrowUp className="h-3 w-3" />{r.upvotes}
                </span>
              )}
              {r.comment_count > 0 && (
                <span className="pf-mono text-[10px] text-muted-foreground flex items-center gap-1">
                  <MessageSquare className="h-3 w-3" />{r.comment_count}
                </span>
              )}
              <span className="text-[10px] text-muted-foreground ml-auto">
                {new Date(r.created_at).toLocaleString('nl-BE', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}
              </span>
            </div>
            {r.body && <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{r.body}</p>}
          </button>
        )
      })}
    </div>
  )
}

/* ─────────── Detail-view met comments + attachments ─────────── */
function ReportDetail({ id, onClose, currentUser, isAdmin }) {
  const [data, setData] = useState(null)
  const [busy, setBusy] = useState(false)
  const [comment, setComment] = useState('')
  const [files, setFiles] = useState([])

  const laad = useCallback(async () => {
    try {
      const r = await reportsAPI.detail(id)
      setData(r.data)
    } catch (e) {
      setData({ err: e.response?.data?.error || e.message })
    }
  }, [id])
  useEffect(() => { laad() }, [laad])

  const vote = async () => {
    setBusy(true)
    try { await reportsAPI.toggleUpvote(id); laad() }
    catch (e) { window.alert(e.response?.data?.error || e.message) }
    finally { setBusy(false) }
  }
  const post = async (e) => {
    e.preventDefault()
    if (!comment.trim()) return
    setBusy(true)
    try {
      await reportsAPI.addComment(id, comment.trim())
      setComment(''); laad()
    } catch (e) { window.alert(e.response?.data?.error || e.message) }
    finally { setBusy(false) }
  }
  const upload = async () => {
    if (!files.length) return
    setBusy(true)
    try { await reportsAPI.uploadFiles(id, files); setFiles([]); laad() }
    catch (e) { window.alert(e.response?.data?.error || e.message) }
    finally { setBusy(false) }
  }
  const delAttach = async (aid) => {
    if (!window.confirm('Bijlage verwijderen?')) return
    setBusy(true)
    try { await reportsAPI.deleteAttachment(aid); laad() }
    catch (e) { window.alert(e.response?.data?.error || e.message) }
    finally { setBusy(false) }
  }

  if (!data) return <Card><CardContent className="p-6 text-muted-foreground">Laden…</CardContent></Card>
  if (data.err) return <Card><CardContent className="p-6 text-signal">{data.err}</CardContent></Card>

  const { report, comments, attachments } = data
  const meta = STATUS_META[report.status] || STATUS_META.open
  const kanCommenten = isAdmin || report.user_id === currentUser?.id || report.my_vote

  return (
    <div className="space-y-4">
      <div>
        <button onClick={onClose} className="text-xs text-muted-foreground hover:text-foreground">
          ← Terug naar overzicht
        </button>
      </div>

      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-start gap-3">
            <button type="button" onClick={vote} disabled={busy}
                    className={`flex flex-col items-center min-w-[3.5rem] py-2 rounded transition-colors ${
                      report.my_vote ? 'bg-acid/10 text-acid border border-acid/40'
                                     : 'text-muted-foreground border border-border hover:border-acid/40'
                    }`}>
              <ArrowUp className="h-5 w-5" />
              <span className="text-base font-bold">{report.upvotes}</span>
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {report.type === 'bug' ? <Bug className="h-4 w-4 text-signal" /> : <Lightbulb className="h-4 w-4 text-acid" />}
                <h2 className="text-lg font-bold">{report.title}</h2>
                <Badge className={`text-[10px] ${meta.color}`}>{meta.label}</Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                door {report.display_name || report.username} · {new Date(report.created_at).toLocaleString('nl-BE')}
              </p>
              {report.body && <p className="text-sm mt-3 whitespace-pre-wrap">{report.body}</p>}
            </div>
          </div>

          {attachments.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {attachments.map(a => (
                <div key={a.id} className="relative group">
                  <a href={a.url} target="_blank" rel="noopener noreferrer">
                    <img src={a.url} alt={a.filename}
                         className="w-full h-32 object-cover rounded border border-border" />
                  </a>
                  {(isAdmin || a.uploaded_by === currentUser?.id) && (
                    <button onClick={() => delAttach(a.id)}
                            className="absolute top-1 right-1 p-1 rounded bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity">
                      <X className="h-3 w-3 text-signal" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {(isAdmin || report.user_id === currentUser?.id) && (
            <div className="flex items-center gap-2 pt-2 border-t border-border/40">
              <label className="inline-flex items-center gap-1 h-8 px-3 text-xs rounded-md border border-input bg-background cursor-pointer hover:border-acid/50">
                <Paperclip className="h-3 w-3" /> Screenshot toevoegen
                <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" multiple className="hidden"
                       onChange={e => setFiles(Array.from(e.target.files || []).slice(0, 3))}
                       disabled={busy} />
              </label>
              {files.length > 0 && (
                <>
                  <span className="text-[10px] text-muted-foreground">{files.length} gekozen</span>
                  <Button size="sm" onClick={upload} disabled={busy}>Upload</Button>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <MessageSquare className="h-4 w-4" /> Reacties ({comments.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {comments.length === 0 && <p className="text-xs text-muted-foreground">Nog geen reacties.</p>}
          {comments.map(c => (
            <div key={c.id} className={`rounded-md border p-3 ${
              c.is_admin ? 'border-acid/30 bg-acid/5' : 'border-border/60 bg-card/50'
            }`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-semibold">{c.display_name || c.username}</span>
                {c.is_admin && <Badge className="text-[10px] bg-acid/20 text-acid border-acid/40">Admin</Badge>}
                <span className="text-[10px] text-muted-foreground ml-auto">
                  {new Date(c.created_at).toLocaleString('nl-BE', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}
                </span>
              </div>
              <p className="text-sm whitespace-pre-wrap">{c.body}</p>
            </div>
          ))}
          {kanCommenten ? (
            <form onSubmit={post} className="pt-3 border-t border-border/40 space-y-2">
              <Textarea value={comment} onChange={e => setComment(e.target.value)}
                        placeholder="Schrijf een reactie…" rows={2} maxLength={3000}
                        disabled={busy} />
              <Button type="submit" size="sm" disabled={busy || !comment.trim()}>
                <Send className="h-3 w-3 mr-1" /> Verzenden
              </Button>
            </form>
          ) : (
            <p className="text-[11px] text-muted-foreground pt-3 border-t border-border/40">
              Upvote eerst als je een reactie wil plaatsen.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
